import { Text, Tooltip } from '@mantine/core';
import { DataTable, type DataTableColumn, type DataTableSortStatus } from 'mantine-datatable';
import { StatusBadge } from '../../../components';
import { PAGE_SIZE } from '../../../magic-values';
import { formatDate } from './format-date';
import type { JobTask } from '../../../types/job-task';

interface JobTasksTableProps {
  tasks: JobTask[];
  fetching?: boolean;
  onRowClick?: (task: JobTask) => void;
  page: number;
  totalRecords: number;
  onPageChange: (page: number) => void;
  sortStatus: DataTableSortStatus;
  onSortStatusChange: (status: DataTableSortStatus) => void;
  height?: string | number;
}

const COLUMNS: DataTableColumn<JobTask>[] = [
  {
    accessor: 'type',
    title: 'Type',
    render: (task) => <Text size="sm">{task.type}</Text>,
  },
  {
    accessor: 'status',
    title: 'Status',
    render: (task) => <StatusBadge status={task.status} />,
  },
  {
    accessor: 'attempt',
    title: 'Attempt',
  },
  {
    accessor: 'origin',
    title: 'Origin',
    render: (task) =>
      task.origin ? (
        <Text size="xs" sx={{ fontFamily: 'monospace' }}>
          {task.origin}
        </Text>
      ) : (
        '—'
      ),
  },
  {
    accessor: 'createdAt',
    title: 'Created',
    sortable: true,
    render: (task) => formatDate(task.createdAt),
  },
  {
    accessor: 'startedAt',
    title: 'Started',
    sortable: true,
    render: (task) => formatDate(task.startedAt),
  },
  {
    accessor: 'finishedAt',
    title: 'Finished',
    sortable: true,
    render: (task) => formatDate(task.finishedAt),
  },
  {
    accessor: 'error',
    title: 'Error',
    render: (task) =>
      task.error ? (
        <Tooltip label={task.error.message} multiline width={320} withArrow>
          <Text size="xs" color="red" sx={{ fontFamily: 'monospace' }}>
            {task.error.code}
          </Text>
        </Tooltip>
      ) : (
        '—'
      ),
  },
];

export function JobTasksTable({
  tasks,
  fetching,
  onRowClick,
  page,
  totalRecords,
  onPageChange,
  sortStatus,
  onSortStatusChange,
  height = 'calc(100vh - 500px)',
}: JobTasksTableProps) {
  return (
    <DataTable
      striped
      highlightOnHover
      height={height}
      sx={{ minWidth: 1000 }}
      records={tasks}
      idAccessor="_id"
      fetching={fetching}
      noRecordsText="No tasks."
      columns={COLUMNS}
      onRowClick={onRowClick ? (task) => onRowClick(task) : undefined}
      totalRecords={totalRecords}
      recordsPerPage={PAGE_SIZE}
      page={page}
      onPageChange={onPageChange}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
    />
  );
}
